import apiClient from '../config/api';
import { Devis, DevisRequestDTO, LigneDevis, LigneDocumentRequestDTO, SousLigneDocumentDTO, TypeLigneDevis } from '../types';
import { calculateDevisTotals } from '../utils/calculations';

/**
 * Transforme une LigneDevis (frontend) en LigneDocumentRequestDTO (backend)
 */
function transformLigneToDTO(ligne: LigneDevis, index: number, tauxTVADevis: number): LigneDocumentRequestDTO {
  let quantite: number;
  let unite: string;

  switch (ligne.type) {
    case TypeLigneDevis.SURFACE:
      // Calculer la quantité finale (surface avec ou sans pertes)
      let surfaceBase = 0;

      if (ligne.modeSaisieSurface === 'direct' && ligne.surface) {
        // Convertir en nombre pour éviter les erreurs de type
        surfaceBase = Number(ligne.surface) || 0;
      } else if (ligne.longueur && ligne.largeur) {
        // Convertir en nombre pour éviter les erreurs de type
        surfaceBase = (Number(ligne.longueur) || 0) * (Number(ligne.largeur) || 0);
      }

      quantite = ligne.avecPertes ? surfaceBase * 1.1 : surfaceBase;
      unite = 'm²';
      break;

    case TypeLigneDevis.UNITAIRE:
      // Convertir en nombre
      quantite = Number(ligne.quantite) || 1;
      unite = 'unité';
      break;

    case TypeLigneDevis.FORFAIT:
      quantite = 1;
      unite = 'forfait';
      break;

    default:
      // Convertir en nombre
      quantite = Number(ligne.quantite) || 1;
      unite = 'unité';
  }

  // S'assurer que quantite est un nombre avant d'appeler toFixed
  const quantiteNum = Number(quantite) || 0;

  return {
    description: ligne.description,
    quantite: parseFloat(quantiteNum.toFixed(2)),
    unite,
    prixUnitaireHT: parseFloat((Number(ligne.prixUnitaire) || 0).toFixed(2)),
    tauxTVA: parseFloat(tauxTVADevis.toFixed(2)), // Utiliser le taux TVA du devis
    ordre: index + 1,
  };
}

/**
 * Transforme une sous-ligne (consommable) en SousLigneDocumentDTO
 */
function transformSousLigneToDTO(sousLigne: LigneDevis, index: number, tauxTVADevis: number): SousLigneDocumentDTO {
  return {
    description: sousLigne.description,
    quantite: parseFloat((Number(sousLigne.quantite) || 0).toFixed(2)),
    unite: 'kg', // Les consommables sont toujours en kg
    prixUnitaireHT: parseFloat((Number(sousLigne.prixUnitaire) || 0).toFixed(2)),
    tauxTVA: parseFloat(tauxTVADevis.toFixed(2)),
    ordre: index + 1,
  };
}

/**
 * Transforme les données du frontend vers le DTO backend pour créer un devis
 * Envoie les sous-lignes dans la structure hiérarchique
 */
function transformToDevisRequestDTO(
  projetId: number,
  data: Omit<Devis, 'id' | 'numero' | 'createdAt' | 'updatedAt'>
): DevisRequestDTO {
  // Recalculer les totaux en incluant les sous-lignes
  const tauxTVANum = Number(data.tauxTVA) || 0;
  const totalsRecalcules = calculateDevisTotals(data.lignes || [], tauxTVANum);
  const montantHTNum = totalsRecalcules.sousTotal;

  // Extraire la partie date (YYYY-MM-DD) des dates ISO
  const dateEmission = (data.dateEmission || data.dateCreation || '').includes('T')
    ? (data.dateEmission || data.dateCreation || '').split('T')[0]
    : (data.dateEmission || data.dateCreation || '');
  const dateValidite = data.dateValidite.includes('T')
    ? data.dateValidite.split('T')[0]
    : data.dateValidite;

  // Transformer les lignes avec leurs sous-lignes
  const lignesTransformees: LigneDocumentRequestDTO[] = (data.lignes || []).map((ligne, index) => {
    const ligneDTO = transformLigneToDTO(ligne, index, tauxTVANum);

    // Ajouter les sous-lignes si elles existent
    if (ligne.sousLignes && ligne.sousLignes.length > 0) {
      ligneDTO.sousLignes = ligne.sousLignes.map((sousLigne, sousIndex) =>
        transformSousLigneToDTO(sousLigne, sousIndex, tauxTVANum)
      );
    }

    return ligneDTO;
  });

  const dto: DevisRequestDTO = {
    projetId,
    dateEmission, // Format YYYY-MM-DD
    dateValidite, // Format YYYY-MM-DD
    statut: data.statut,
    montantHT: parseFloat(montantHTNum.toFixed(2)),
    tauxTVA: parseFloat(tauxTVANum.toFixed(2)),
    typeRemise: data.typeRemise, // Type de remise (POURCENTAGE ou MONTANT_FIXE)
    valeurRemise: data.valeurRemise ? parseFloat(Number(data.valeurRemise).toFixed(2)) : undefined, // Valeur de la remise
    conditions: data.conditions || '',
    notes: data.notes || '',
    lignes: lignesTransformees,
    signatureClient: data.signatureClient,
    dateSignature: data.dateSignature,
  };

  // Validation des données
  if (!dto.projetId || dto.projetId <= 0) {
    throw new Error('ProjetId invalide');
  }
  if (!dto.dateEmission || !dto.dateValidite) {
    throw new Error('Dates invalides');
  }
  if (!dto.lignes || dto.lignes.length === 0) {
    throw new Error('Le devis doit contenir au moins une ligne');
  }

  return dto;
}

class DevisService {
  /**
   * Récupérer tous les devis
   */
  async getAll(): Promise<Devis[]> {
    const response = await apiClient.get<Devis[]>('/api/devis');
    return response.data;
  }

  /**
   * Récupérer un devis par son ID
   */
  async getById(id: number): Promise<Devis> {
    console.log('📥 Récupération du devis ID:', id);
    try {
      const response = await apiClient.get<Devis>(`/api/devis/${id}`);
      console.log('✅ Devis récupéré:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Erreur lors de la récupération du devis:');
      console.error('- URL:', error.config?.url);
      console.error('- Status:', error.response?.status);
      console.error('- Réponse:', error.response?.data);
      throw error;
    }
  }

  /**
   * Créer un nouveau devis
   * @param projetId - ID du projet associé
   * @param data - Données du devis (format frontend)
   */
  async create(
    projetId: number,
    data: Omit<Devis, 'id' | 'numero' | 'createdAt' | 'updatedAt'>
  ): Promise<Devis> {
    console.log('📥 Données reçues (avant transformation):', JSON.stringify(data, null, 2));
    console.log('📍 ProjetId:', projetId, 'Type:', typeof projetId);

    const dto = transformToDevisRequestDTO(projetId, data);
    console.log('📤 Données envoyées au backend (DTO):', JSON.stringify(dto, null, 2));

    try {
      const response = await apiClient.post<Devis>('/api/devis', dto);
      return response.data;
    } catch (error: any) {
      console.error('❌ Erreur détaillée de la requête:');
      console.error('- URL:', error.config?.url);
      console.error('- Méthode:', error.config?.method);
      console.error('- Headers:', error.config?.headers);
      console.error('- Data envoyée:', error.config?.data);
      console.error('- Réponse du serveur:', error.response?.data);
      console.error('- Status:', error.response?.status);
      throw error;
    }
  }

  /**
   * Mettre à jour un devis
   */
  async update(id: number, data: Partial<Devis>): Promise<Devis> {
    // Si le devis contient un projetId et des lignes, transformer en DTO
    if (data.projetId && data.lignes) {
      const dto = transformToDevisRequestDTO(
        data.projetId,
        data as Omit<Devis, 'id' | 'numero' | 'createdAt' | 'updatedAt'>
      );
      console.log('📤 DTO envoyé au backend:', {
        projetId: dto.projetId,
        statut: dto.statut,
        hasSignature: !!dto.signatureClient,
        signatureLength: dto.signatureClient?.length,
        dateSignature: dto.dateSignature,
      });
      const response = await apiClient.put<Devis>(`/api/devis/${id}`, dto);
      console.log('📥 Réponse du backend:', {
        id: response.data.id,
        statut: response.data.statut,
        hasSignature: !!response.data.signatureClient,
        dateSignature: response.data.dateSignature,
      });
      return response.data;
    }

    // Sinon, envoyer les données telles quelles (pour les mises à jour partielles)
    console.log('📤 Données partielles envoyées:', data);
    const response = await apiClient.put<Devis>(`/api/devis/${id}`, data);
    console.log('📥 Réponse du backend:', response.data);
    return response.data;
  }

  /**
   * Supprimer un devis
   */
  async delete(id: number): Promise<void> {
    await apiClient.delete(`/api/devis/${id}`);
  }

  /**
   * Récupérer les devis d'un client
   */
  async getByClientId(clientId: number): Promise<Devis[]> {
    const response = await apiClient.get<Devis[]>(`/api/devis/client/${clientId}`);
    return response.data;
  }

  /**
   * Dupliquer un devis
   */
  async duplicate(id: number): Promise<Devis> {
    const response = await apiClient.post<Devis>(`/api/devis/${id}/duplicate`);
    return response.data;
  }

  /**
   * Changer le statut d'un devis
   */
  async updateStatus(id: number, statut: string): Promise<Devis> {
    const response = await apiClient.patch<Devis>(`/api/devis/${id}/status`, { statut });
    return response.data;
  }

  /**
   * Sauvegarder la signature du client
   */
  async saveSignature(id: number, signatureData: string, dateSignature: string): Promise<Devis> {
    console.log('📝 Tentative de sauvegarde de la signature via PATCH:', {
      id,
      signatureLength: signatureData.length,
      dateSignature,
    });

    try {
      // Essayer d'abord avec un endpoint dédié à la signature
      const response = await apiClient.patch<Devis>(`/api/devis/${id}/signature`, {
        signatureClient: signatureData,
        dateSignature,
        statut: 'ACCEPTE',
      });
      console.log('✅ Signature sauvegardée via endpoint /signature');
      return response.data;
    } catch (error: any) {
      // Si l'endpoint n'existe pas, essayer avec PATCH général
      if (error.response?.status === 404) {
        console.log('⚠️ Endpoint /signature non trouvé, utilisation de PATCH général');
        const response = await apiClient.patch<Devis>(`/api/devis/${id}`, {
          signatureClient: signatureData,
          dateSignature,
          statut: 'ACCEPTE',
        });
        console.log('✅ Signature sauvegardée via PATCH général');
        return response.data;
      }
      throw error;
    }
  }

  /**
   * Télécharger le PDF d'un devis généré par le backend
   * @param id - ID du devis
   * @returns Le PDF en tant que Blob
   */
  async downloadPDF(id: number): Promise<Blob> {
    console.log('📄 Téléchargement du PDF du devis ID:', id);
    try {
      const response = await apiClient.get(`/api/devis/${id}/pdf`, {
        responseType: 'blob',
      });
      console.log('✅ PDF téléchargé, taille:', response.data.size, 'bytes');
      return response.data;
    } catch (error: any) {
      console.error('❌ Erreur lors du téléchargement du PDF du devis:', error);
      throw error;
    }
  }

  /**
   * Obtenir le PDF d'un devis en base64 généré par le backend
   * @param id - ID du devis
   * @returns Le PDF encodé en base64
   */
  async getPDFBase64(id: number): Promise<string> {
    console.log('📄 Récupération du PDF en base64 du devis ID:', id);
    try {
      const blob = await this.downloadPDF(id);

      // Convertir le Blob en base64
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = reader.result as string;
          // Extraire la partie base64 (après "data:application/pdf;base64,")
          const base64Data = base64.split(',')[1];
          console.log('✅ PDF converti en base64, taille:', base64Data.length, 'caractères');
          resolve(base64Data);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error: any) {
      console.error('❌ Erreur lors de la conversion du PDF en base64:', error);
      throw error;
    }
  }
}

export default new DevisService();
